import { Edit3, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import EmptyState from "../../components/EmptyState.jsx";
import { Field, Toggle } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import SearchBar from "../../components/SearchBar.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { deleteRecord, getMasterData, saveRecord } from "../../firebase/firestore.js";

const emptySubject = { name: "", color: "#0ea5e9", icon: "📘", active: true };

export default function SubjectManager() {
  const [master, setMaster] = useState(null);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  async function load(force = false) {
    setLoading(true);
    try {
      setMaster(await getMasterData(force));
    } catch (error) {
      showToast(error.message || "Unable to load subjects", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const subjects = useMemo(() => {
    return (master?.subjects || []).filter((subject) =>
      `${subject.name} ${subject.icon}`.toLowerCase().includes(search.toLowerCase())
    );
  }, [master, search]);

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await saveRecord("subjects", modal.form, modal.id);
      showToast("Subject saved");
      setModal(null);
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to save subject", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteRecord("subjects", confirm.id);
      showToast("Subject deleted");
      setConfirm(null);
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to delete subject", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="admin-page">
      <div className="page-title-row">
        <div>
          <p>Master Data</p>
          <h2>Subjects</h2>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => setModal({ id: null, form: emptySubject })}>
          <Plus size={18} />
          Add Subject
        </button>
      </div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search subject" />
      {loading ? (
        <SkeletonList />
      ) : subjects.length ? (
        <div className="manager-grid">
          {subjects.map((subject) => (
            <article className="card manager-card" key={subject.id}>
              <div className="subject-dot" style={{ background: subject.color }}>{subject.icon || "📘"}</div>
              <div>
                <p className="eyebrow">{subject.active === false ? "Inactive" : "Active"}</p>
                <h3>{subject.name}</h3>
                <p>{subject.color}</p>
              </div>
              <div className="card-actions">
                <button className="icon-button" type="button" onClick={() => setModal({ id: subject.id, form: { ...emptySubject, ...subject } })}><Edit3 size={18} /></button>
                <button className="icon-button danger" type="button" onClick={() => setConfirm(subject)}><Trash2 size={18} /></button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState title="No subjects found" />
      )}
      <Modal open={Boolean(modal)} title={modal?.id ? "Edit Subject" : "Add Subject"} onClose={() => setModal(null)}>
        <form className="form-grid" onSubmit={handleSave}>
          <Field label="Subject name"><input value={modal?.form.name || ""} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, name: e.target.value } }))} required /></Field>
          <Field label="Icon or emoji"><input value={modal?.form.icon || ""} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, icon: e.target.value } }))} /></Field>
          <Field label="Color"><input type="color" value={modal?.form.color || "#0ea5e9"} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, color: e.target.value } }))} /></Field>
          <Toggle label="Active subject" checked={modal?.form.active !== false} onChange={(value) => setModal((m) => ({ ...m, form: { ...m.form, active: value } }))} />
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : null}Save</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog open={Boolean(confirm)} title="Delete subject?" message={`Delete ${confirm?.name || "this subject"}?`} loading={saving} onCancel={() => setConfirm(null)} onConfirm={handleDelete} />
    </section>
  );
}
